
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Filter, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { SiteDialog } from "./SiteDialog";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import { toast } from "sonner";

export const Sites = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [selectedOrg, setSelectedOrg] = useState<string>("all");
  const [selectedSite, setSelectedSite] = useState<{id: string; name: string} | null>(null);
  const queryClient = useQueryClient();

  const { data: regions } = useQuery({
    queryKey: ["regions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regions")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  const { data: organizations } = useQuery({
    queryKey: ["organizations", selectedRegion],
    queryFn: async () => {
      let query = supabase
        .from("organizations")
        .select("*")
        .order("name");
      
      if (selectedRegion !== "all") {
        query = query.eq('region_id', selectedRegion);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  const { data: sites, isLoading } = useQuery({
    queryKey: ["sites", selectedRegion, selectedOrg],
    queryFn: async () => {
      // First, get all organizations if we're filtering by region
      let orgIds: string[] = [];
      
      if (selectedRegion !== "all" && selectedOrg === "all") {
        const { data: orgs } = await supabase
          .from("organizations")
          .select("id")
          .eq('region_id', selectedRegion);
          
        if (orgs) {
          orgIds = orgs.map(org => org.id);
        }
      }
      
      // Now build the main query
      let query = supabase
        .from("sites")
        .select(`
          *,
          organization:organizations(
            id,
            name,
            region:regions(id, name)
          )
        `)
        .order('name');
      
      // Apply filters
      if (selectedOrg !== "all") {
        // Filter by specific organization
        query = query.eq('organization_id', selectedOrg);
      } else if (selectedRegion !== "all" && orgIds.length > 0) {
        // Filter by all organizations in the selected region
        query = query.in('organization_id', orgIds);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  const handleEdit = (site: {id: string; name: string}) => {
    setSelectedSite(site);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedSite) return;

    try {
      const { error } = await supabase
        .from("sites")
        .delete()
        .eq("id", selectedSite.id);

      if (error) throw error;

      toast.success("Site deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      setIsDeleteDialogOpen(false);
      setSelectedSite(null);
    } catch (error) {
      console.error("Error deleting site:", error);
      toast.error("Failed to delete site");
    }
  };

  const confirmDelete = (site: {id: string; name: string}) => {
    setSelectedSite(site);
    setIsDeleteDialogOpen(true);
  };

  if (isLoading) {
    return <div>Loading sites...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={selectedRegion} onValueChange={(value) => {
            setSelectedRegion(value);
            setSelectedOrg("all");
          }}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {regions?.map((region) => (
                <SelectItem key={region.id} value={region.id}>
                  {region.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedOrg} onValueChange={setSelectedOrg}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by organization" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
              {organizations?.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => {
          setSelectedSite(null);
          setIsDialogOpen(true);
        }}>
          <Plus className="mr-2 h-4 w-4" />
          Add Site
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sites?.map((site) => (
          <div key={site.id} className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{site.name}</h3>
                <p className="text-sm text-muted-foreground">
                  Organization: {site.organization?.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  Region: {site.organization?.region?.name}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleEdit(site)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => confirmDelete(site)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <SiteDialog 
        open={isDialogOpen} 
        onOpenChange={setIsDialogOpen}
        siteToEdit={selectedSite}
      />

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Delete Site"
        description={`Are you sure you want to delete ${selectedSite?.name}? This action cannot be undone.`}
      />
    </div>
  );
};
